// AdminApp.jsx - The dashboard entry point. Holds the session, gates every route
// behind it, and renders the sign in screen when there is no session.
//
// Everything under here assumes an authenticated user, so nothing below has to
// check again.
//
// The dashboard is also the half of this build that installs to a home screen,
// so its service worker is registered here: once, when the chunk loads, and
// never on the customer site.
//
// LEGEND
//   1. AuthContext / useAuth   session, permissions, sign in and out
//   2. AdminApp                the gate and the route tree

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate } from "react-router";

import { authApi } from "../lib/api.js";
import { registerWorker } from "../lib/pwa.js";
import { Loading, ToastProvider } from "../ui/kit.jsx";

import AdminLayout from "./AdminLayout.jsx";
import Login from "./Login.jsx";
import Dashboard from "./Dashboard.jsx";
import Orders from "./Orders.jsx";
import Leads from "./Leads.jsx";
import OrderDetail from "./OrderDetail.jsx";
import Kits, { KitSheet } from "./Kits.jsx";
import Shows, { ShowDetail } from "./Shows.jsx";
import Inbox from "./Inbox.jsx";
import Activity from "./Activity.jsx";
import Settings from "./Settings.jsx";
import Team from "./Team.jsx";
import Affiliates from "./Affiliates.jsx";
import Cards from "./Cards.jsx";

////////// 1. SESSION //////////

const AuthContext = createContext(null);

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth was called outside the dashboard");
  return value;
}

function useSession() {
  const [state, setState] = useState({ user: null, permissions: [], notifyEvents: [], roles: [], loading: true });

  const load = useCallback(async () => {
    try {
      const data = await authApi.me();
      setState({ ...data, loading: false });
    } catch {
      setState({ user: null, permissions: [], notifyEvents: [], roles: [], loading: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const signIn = useCallback(async (credentials) => {
    await authApi.login(credentials);
    await load();
  }, [load]);

  const signOut = useCallback(async () => {
    await authApi.logout().catch(() => {});
    setState({ user: null, permissions: [], notifyEvents: [], roles: [], loading: false });
  }, []);

  // "*" is the owner grant. Everything else is an exact permission string.
  const can = useCallback(
    (permission) => state.permissions.includes("*") || state.permissions.includes(permission),
    [state.permissions]
  );

  return useMemo(() => ({ ...state, signIn, signOut, can, reload: load }), [state, signIn, signOut, can, load]);
}

////////// 2. APP //////////

export default function AdminApp() {
  const auth = useSession();

  // Registering is safe to repeat and safe to fail. A browser without service
  // workers, or a session over plain http, simply gets an ordinary web page.
  useEffect(() => { registerWorker(); }, []);

  if (auth.loading) return <Loading label="Checking your session" className="min-h-screen" />;

  return (
    <AuthContext.Provider value={auth}>
      <ToastProvider>
        {auth.user ? (
          <Routes>
            <Route element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:number" element={<OrderDetail />} />
              <Route path="kits" element={<Kits />} />
              <Route path="shows" element={<Shows />} />
              <Route path="shows/:slug" element={<ShowDetail />} />
              <Route path="bounced" element={<Leads />} />
              <Route path="inbox" element={<Inbox />} />
              <Route path="activity" element={<Activity />} />
              <Route path="settings" element={<Settings />} />
              <Route path="team" element={<Team />} />
              <Route path="affiliates" element={<Affiliates />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>
            {/* Outside the frame: this one is printed, and a sidebar does not
                belong on paper. */}
            <Route path="kits/print" element={<KitSheet />} />
            <Route path="affiliates/cards" element={<Cards />} />
          </Routes>
        ) : (
          <Login onSignIn={auth.signIn} />
        )}
      </ToastProvider>
    </AuthContext.Provider>
  );
}
